import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  DoneAll as DoneAllIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import notificationApi, { Notification, NotificationsResponse } from '../services/notificationApi';

const UNREAD_COUNT_KEY = ['notifications', 'unread-count'];
const LIST_PARAMS = { limit: 10, include_read: true };
const LIST_KEY = ['notifications', 'list', LIST_PARAMS];

interface NotificationBellProps {
  onNotificationClick?: (notification: Notification) => void;
}

export default function NotificationBell({ onNotificationClick }: NotificationBellProps) {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  
  // ─── DETAIL MODAL STATE ──────────────────────────────────────────────────
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  // ─── THEME COLORS ──────────────────────────────────────────────────────────
  const colors = {
    text: isDark ? '#e2e8f0' : '#0f172a',
    muted: isDark ? '#94a3b8' : '#64748b',
    background: isDark ? '#0d0d0d' : '#ffffff',
    cardBg: isDark ? '#0d0d0d' : '#ffffff',
    border: isDark ? '#1f1f1f' : '#e2e8f0',
    hover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    popoverBg: isDark ? '#1a1a1a' : '#ffffff',
    popoverBorder: isDark ? '#333333' : '#e5e7eb',
  };

  // ─── UNREAD COUNT (live events + 60s fallback poll) ──────────────────────
  const unreadCountQuery = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: async () => ((await notificationApi.getUnreadCount()).unread_count || 0) as number,
    enabled: !!user,
    refetchInterval: 60_000,
  });
  const unreadCount = unreadCountQuery.data ?? 0;

  // ─── NOTIFICATIONS (fetched while the popover is open) ───────────────────
  // ✅ includeRead: true - Show ALL notifications (read + unread)
  const notificationsQuery = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const response: NotificationsResponse = await notificationApi.getNotifications(LIST_PARAMS.limit, LIST_PARAMS.include_read);
      queryClient.setQueryData(UNREAD_COUNT_KEY, response.unread_count || 0);
      return response.notifications || [];
    },
    enabled: !!user && open,
  });
  const notifications = notificationsQuery.data ?? [];
  const loading = notificationsQuery.isLoading;
  const error = notificationsQuery.isError && !notificationsQuery.data ? 'Failed to load notifications' : null;

  // ─── MARK AS READ (optimistic, rolled back on failure) ───────────────────
  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationApi.markAsRead(notificationId),
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousList = queryClient.getQueryData<Notification[]>(LIST_KEY);
      const previousCount = queryClient.getQueryData<number>(UNREAD_COUNT_KEY);
      queryClient.setQueryData<Notification[]>(LIST_KEY, (prev: Notification[] | undefined) =>
        prev?.map((n: Notification) => (n.id === notificationId ? { ...n, is_read: true } : n)));
      queryClient.setQueryData<number>(UNREAD_COUNT_KEY, (prev: number | undefined) => Math.max(0, (prev ?? 0) - 1));
      return { previousList, previousCount };
    },
    onError: (err, _id, context) => {
      console.error('Failed to mark as read:', err);
      if (context?.previousList) queryClient.setQueryData(LIST_KEY, context.previousList);
      if (context?.previousCount !== undefined) queryClient.setQueryData(UNREAD_COUNT_KEY, context.previousCount);
    },
    meta: { invalidates: [['notifications']] },
  });

  // ─── MARK ALL AS READ ────────────────────────────────────────────────────
  const markAllMutation = useMutation({
    mutationFn: () => notificationApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.setQueryData<Notification[]>(LIST_KEY, (prev: Notification[] | undefined) => prev?.map((n: Notification) => ({ ...n, is_read: true })));
      queryClient.setQueryData(UNREAD_COUNT_KEY, 0);
    },
    onError: err => console.error('Failed to mark all as read:', err),
    meta: { invalidates: [['notifications']] },
  });
  const markingAll = markAllMutation.isPending;

  const handleMarkAsRead = (notificationId: string) => markReadMutation.mutate(notificationId);
  const handleMarkAllAsRead = () => markAllMutation.mutate();

  // ─── HANDLE BELL CLICK ────────────────────────────────────────────────────
  const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    if (notificationsQuery.data) notificationsQuery.refetch();
  };

  // ─── HANDLE CLOSE ─────────────────────────────────────────────────────────
  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  // ─── HANDLE NOTIFICATION CLICK ───────────────────────────────────────────
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    
    // Open detail modal
    setSelectedNotification(notification);
    setDetailModalOpen(true);
    handleClose();
    
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  // ─── HANDLE DETAIL MODAL CLOSE ───────────────────────────────────────────
  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedNotification(null);
  };

  // ─── PRIORITY STYLES ──────────────────────────────────────────────────────
  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'high':
        return {
          bgColor: isDark ? '#7f1d1d' : '#fee2e2',
          color: '#dc2626',
          borderColor: isDark ? '#991b1b' : '#fca5a5',
          icon: '🔴',
          label: 'Critical',
        };
      case 'medium':
        return {
          bgColor: isDark ? '#78350f' : '#fef3c7',
          color: '#d97706',
          borderColor: isDark ? '#92400e' : '#fcd34d',
          icon: '⚠️',
          label: 'Warning',
        };
      case 'low':
      default:
        return {
          bgColor: isDark ? '#1e3a5f' : '#dbeafe',
          color: '#2563eb',
          borderColor: isDark ? '#1e4a7a' : '#93c5fd',
          icon: 'ℹ️',
          label: 'Info',
        };
    }
  };

  // ─── FORMAT DATE ──────────────────────────────────────────────────────────
  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────

  const openPopover = Boolean(anchorEl);

  // ─── DETAIL MODAL ─────────────────────────────────────────────────────────
  const renderDetailModal = () => {
    if (!selectedNotification) return null;
    
    const priorityStyles = getPriorityStyles(selectedNotification.priority);
    
    return (
      <Dialog
        open={detailModalOpen}
        onClose={handleDetailModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          color: colors.text, 
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 20 }}>{priorityStyles.icon}</span>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.text }}>
              {selectedNotification.title}
            </Typography>
          </Box>
          <IconButton onClick={handleDetailModalClose} sx={{ color: colors.muted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Chip
              label={priorityStyles.label}
              size="small"
              sx={{
                bgcolor: priorityStyles.bgColor,
                color: priorityStyles.color,
                fontWeight: 600,
              }}
            />
            <Chip
              label={formatTimeAgo(selectedNotification.sent_at)}
              size="small"
              sx={{
                ml: 1,
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
                color: colors.muted,
              }}
            />
          </Box>
          <Typography variant="body1" sx={{ color: colors.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {selectedNotification.message}
          </Typography>
          {selectedNotification.expires_at && (
            <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mt: 2 }}>
              Expires: {new Date(selectedNotification.expires_at).toLocaleString()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={handleDetailModalClose}
            sx={{
              color: colors.muted,
              '&:hover': { bgcolor: colors.hover },
              textTransform: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      {/* ─── BELL ICON ──────────────────────────────────────────────────────── */}
      <IconButton
        onClick={handleBellClick}
        sx={{
          color: isDark ? '#e2e8f0' : '#333333',
          ml: 1,
          '&:hover': { 
            bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' 
          },
          p: 1,
        }}
        size="medium"
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 11,
              minWidth: 20,
              height: 20,
              padding: '0 4px',
              fontWeight: 700,
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsIcon sx={{ fontSize: 28 }} />
          ) : (
            <NotificationsNoneIcon sx={{ fontSize: 28 }} />
          )}
        </Badge>
      </IconButton>

      {/* ─── POPOVER ────────────────────────────────────────────────────────── */}
      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            mt: 1.5,
            borderRadius: 2,
            bgcolor: colors.popoverBg,
            border: `1px solid ${colors.popoverBorder}`,
            boxShadow: isDark ? '0 4px 30px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          },
        }}
      >
        {/* ─── HEADER ──────────────────────────────────────────────────────── */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${colors.popoverBorder}`,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text }}>
            Notifications
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} new`}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: 11 }}
              />
            )}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
              disabled={markingAll}
              sx={{ 
                textTransform: 'none', 
                fontSize: 12,
                color: colors.text,
              }}
            >
              {markingAll ? 'Marking...' : 'Mark all read'}
            </Button>
          )}
        </Box>

        {/* ─── CONTENT ────────────────────────────────────────────────────── */}
        {error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
            <Button
              size="small"
              onClick={() => notificationsQuery.refetch()}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Retry
            </Button>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: colors.muted, mb: 1 }} />
            <Typography variant="body2" sx={{ color: colors.muted }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflowY: 'auto' }}>
            {notifications.map((notification, index) => {
              const priorityStyles = getPriorityStyles(notification.priority);
              const isUnread = !notification.is_read;
              
              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      bgcolor: isUnread 
                        ? (isDark ? 'rgba(59, 130, 246, 0.08)' : '#f0f7ff')
                        : 'transparent',
                      '&:hover': {
                        bgcolor: isUnread 
                          ? (isDark ? 'rgba(59, 130, 246, 0.15)' : '#e5f0ff')
                          : (isDark ? 'rgba(255,255,255,0.03)' : '#f5f5f5'),
                      },
                      transition: 'background-color 0.15s',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ mr: 1.5, mt: 0.5 }}>
                      <span style={{ fontSize: 20 }}>{priorityStyles.icon}</span>
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isUnread ? 600 : 400,
                              color: isUnread ? colors.text : colors.muted,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          {isUnread && (
                            <Chip
                              label="New"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: 9,
                                bgcolor: '#3b82f6',
                                color: '#fff',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              display: 'block',
                              color: colors.muted,
                              fontSize: 13,
                              mt: 0.5,
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              display: 'block',
                              color: colors.muted,
                              fontSize: 11,
                              mt: 0.5,
                            }}
                          >
                            {formatTimeAgo(notification.sent_at)}
                          </Typography>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    {isUnread && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#3b82f6',
                          flexShrink: 0,
                          ml: 1,
                          mt: 1,
                        }}
                      />
                    )}
                  </ListItem>
                  {index < notifications.length - 1 && (
                    <Divider sx={{ mx: 2, borderColor: colors.popoverBorder }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>

      {/* ─── DETAIL MODAL ──────────────────────────────────────────────────── */}
      {renderDetailModal()}
    </>
  );
}